"use client";

/**
 * On-device network log for the debug bar (components/NetworkDebugBar.tsx).
 *
 * Phones have no devtools, so this records every load the page makes and
 * exposes it as a store for `useSyncExternalStore`:
 *
 * - `window.fetch` is wrapped so in-flight requests, HTTP status and network
 *   failures are visible the moment they happen;
 * - a buffered `PerformanceObserver` supplies the finished timings for every
 *   resource — including scripts, styles, images and anything fetched before
 *   the wrapper was installed — plus the transfer size and the
 *   `Server-Timing` breakdown (`proxy` from the Next route, `app`/`db` from
 *   FastAPI).
 *
 * Nothing is installed until the bar is switched on (localStorage flag, or
 * `?netdebug=1` in the URL), so normal sessions pay nothing.
 */

export interface ServerTimingPart {
  name: string;
  dur: number;
  desc?: string;
}

export interface NetEntry {
  id: number;
  url: string;
  method: string;
  /** `fetch`, or the resource's initiator type (`script`, `img`, `link`, …). */
  kind: string;
  /** `performance.now()` time the request started. */
  startedAt: number;
  /** Null while the request is still in flight. */
  duration: number | null;
  status: number | null;
  /** Bytes over the wire (or the decoded body when the transfer size is hidden). */
  size: number | null;
  serverTiming: ServerTimingPart[];
  error?: string;
  /** Set once the matching resource-timing entry has been folded in. */
  timed?: boolean;
}

const STORAGE_KEY = "lk:netdebug";
const CHANGE_EVENT = "lk:netdebug-change";
const MAX_ENTRIES = 500;

// ---- enabled flag ---------------------------------------------------------

export function isNetworkDebugEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setNetworkDebugEnabled(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(STORAGE_KEY, "1");
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: the toggle just won't survive a reload.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function subscribeNetworkDebugEnabled(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

// ---- eruda ------------------------------------------------------------------

type ErudaGlobal = { init: () => void; destroy: () => void };
const erudaWindow = () => window as unknown as { eruda?: ErudaGlobal };

/**
 * Eruda console alongside the bar. On page load app/layout.tsx injects it
 * before hydration (so it works even when the bundle crashes); this covers
 * switching the flag at runtime.
 */
export function setErudaEnabled(on: boolean): void {
  const eruda = erudaWindow().eruda;
  // `#eruda` is its container: present means it's initialised. `destroy()`
  // throws on a loaded-but-uninitialised instance, so check before either.
  const up = !!document.getElementById("eruda");
  if (!on) {
    if (eruda && up) eruda.destroy();
    return;
  }
  if (eruda) {
    if (!up) eruda.init();
    return;
  }
  if (document.getElementById("eruda-script")) return;
  const s = document.createElement("script");
  s.id = "eruda-script";
  s.src = "https://cdn.jsdelivr.net/npm/eruda";
  s.onload = () => erudaWindow().eruda?.init();
  document.head.appendChild(s);
}

// ---- entry store -----------------------------------------------------------

let entries: NetEntry[] = [];
let snapshot: NetEntry[] = entries;
let nextId = 1;
let emitQueued = false;
const listeners = new Set<() => void>();

/** Coalesce bursts (a page load is dozens of entries) into one render per frame. */
function emit() {
  if (emitQueued) return;
  emitQueued = true;
  requestAnimationFrame(() => {
    emitQueued = false;
    snapshot = entries.map((e) => ({ ...e }));
    listeners.forEach((l) => l());
  });
}

export function subscribeNetworkEntries(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function getNetworkEntries(): NetEntry[] {
  return snapshot;
}

const EMPTY: NetEntry[] = [];
export function getServerNetworkEntries(): NetEntry[] {
  return EMPTY;
}

export function clearNetworkEntries(): void {
  // Keep what is still in flight so its completion has somewhere to land.
  entries = entries.filter((e) => e.duration === null && !e.error);
  emit();
}

function add(entry: Omit<NetEntry, "id">): NetEntry {
  const full = { ...entry, id: nextId++ };
  entries.push(full);
  if (entries.length > MAX_ENTRIES) entries = entries.slice(-MAX_ENTRIES);
  emit();
  return full;
}

// ---- parsing ----------------------------------------------------------------

/** Parses a `Server-Timing` header: `db;dur=12.3;desc="4 queries", app;dur=40`. */
export function parseServerTiming(header: string | null): ServerTimingPart[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part) => {
      const [name, ...params] = part.trim().split(";");
      const out: ServerTimingPart = { name: name.trim(), dur: 0 };
      for (const p of params) {
        const [k, v = ""] = p.trim().split("=");
        if (k === "dur") out.dur = Number(v) || 0;
        if (k === "desc") out.desc = v.replace(/^"|"$/g, "");
      }
      return out;
    })
    .filter((p) => p.name);
}

function absoluteUrl(url: string): string {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
}

// ---- instrumentation ---------------------------------------------------------

let installed = false;

export function installNetworkDebug(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // The default buffer (250) overflows on a long session.
  performance.setResourceTimingBufferSize?.(1000);

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const rawUrl =
      typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const entry = add({
      url: absoluteUrl(rawUrl),
      method,
      kind: "fetch",
      startedAt: performance.now(),
      duration: null,
      status: null,
      size: null,
      serverTiming: [],
    });
    try {
      const res = await originalFetch(input, init);
      entry.status = res.status;
      // Time to headers for now; the resource-timing entry replaces it with
      // the full time once the body has arrived.
      if (!entry.timed) entry.duration = performance.now() - entry.startedAt;
      if (!entry.serverTiming.length) {
        entry.serverTiming = parseServerTiming(res.headers.get("server-timing"));
      }
      const length = res.headers.get("content-length");
      if (entry.size === null && length) entry.size = Number(length);
      emit();
      return res;
    } catch (err) {
      entry.duration = performance.now() - entry.startedAt;
      entry.error =
        err instanceof DOMException && err.name === "AbortError"
          ? "aborted"
          : err instanceof Error
            ? err.message || "network error"
            : "network error";
      emit();
      throw err;
    }
  };

  const ingest = (pe: PerformanceResourceTiming) => {
    const size = pe.transferSize || pe.encodedBodySize || null;
    const serverTiming = (pe.serverTiming ?? []).map((s) => ({
      name: s.name,
      dur: s.duration,
      desc: s.description || undefined,
    }));
    const status =
      "responseStatus" in pe && typeof pe.responseStatus === "number" && pe.responseStatus > 0
        ? pe.responseStatus
        : null;

    if (pe.initiatorType === "fetch" || pe.initiatorType === "xmlhttprequest") {
      const match = entries.find(
        (e) =>
          e.kind === "fetch" &&
          !e.timed &&
          e.url === pe.name &&
          pe.startTime >= e.startedAt - 5
      );
      if (match) {
        match.timed = true;
        match.duration = pe.duration;
        if (size !== null) match.size = size;
        if (serverTiming.length) match.serverTiming = serverTiming;
        if (match.status === null) match.status = status;
        emit();
        return;
      }
    }
    // A request made before the wrapper existed, or a non-fetch resource.
    add({
      url: pe.name,
      method: "GET",
      kind: pe.initiatorType || "other",
      startedAt: pe.startTime,
      duration: pe.duration,
      status,
      size,
      serverTiming,
      timed: true,
    });
  };

  const nav = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (nav) {
    add({
      url: nav.name,
      method: "GET",
      kind: "document",
      startedAt: 0,
      duration: nav.duration || nav.responseEnd,
      status: nav.responseStatus || null,
      size: nav.transferSize || nav.encodedBodySize || null,
      serverTiming: (nav.serverTiming ?? []).map((s) => ({
        name: s.name,
        dur: s.duration,
        desc: s.description || undefined,
      })),
      timed: true,
    });
  }

  try {
    new PerformanceObserver((list) => {
      for (const pe of list.getEntries()) ingest(pe as PerformanceResourceTiming);
    }).observe({ type: "resource", buffered: true });
  } catch {
    // Very old browsers: the fetch wrapper alone still shows API calls.
  }
}
